/**
 * ESPELHO do texto dos Termos de Uso exibido no modal de consentimento.
 *
 * ⚠️ A FONTE ÚNICA é `docs/legal/TERMS_OF_USE.md`. Este arquivo NÃO é o
 * documento: é a transcrição usada para renderizar o texto no CRM sem depender
 * de um parser de markdown. Qualquer alteração material do texto muda o
 * documento PRIMEIRO e depois este espelho, na mesma mudança.
 *
 * ⚠️ Espelhe apenas o TEXTO LEGAL (Cláusulas 1 a 16). As seções finais do
 * documento — "Enxertos pendentes de revisão", "Divergências entre o texto
 * revisado e o produto" e o mapa área ⇒ cláusula — são notas internas de
 * engenharia e NÃO vão para o modal.
 *
 * ⚠️ `TERMS_VERSION` precisa ser IDÊNTICA à `TERMS_OF_USE_VERSION` do backend
 * (`atriva-api/src/modules/terms/domain/terms-version.const.ts`) e ao cabeçalho
 * do documento. O backend recusa aceite de versão divergente com
 * `VALIDATION_TERMS_VERSION_MISMATCH` — divergir aqui trava o modal para todo
 * mundo.
 *
 * Procedimento de bump (os quatro itens, sempre juntos):
 *   1. texto + data + linha no Changelog em `docs/legal/TERMS_OF_USE.md`;
 *   2. `TERMS_OF_USE_VERSION` no backend;
 *   3. `TERMS_VERSION` + seções aqui;
 *   4. uma entrada nova em `TERMS_CHANGELOG` (alimenta a caixa "O que mudou
 *      nesta versão" de quem já havia aceitado uma versão anterior).
 *
 * Bump de versão ⇒ TODOS os usuários voltam a ver o modal no próximo acesso.
 */

/** Versão corrente do documento (`YYYY-MM-DD`). Identificador, não data. */
export const TERMS_VERSION = '2026-09-22';

/**
 * Bloco de conteúdo dentro de uma seção. Duas formas apenas — parágrafo e
 * lista — o suficiente para transcrever o documento (as tabelas do original
 * viram listas no formato `assunto — detalhe`) sem carregar um parser.
 */
export type TermsBlock = { kind: 'p'; text: string } | { kind: 'list'; items: string[] };

export interface TermsSection {
  /** Número da cláusula no documento (1..16). */
  number: number;
  title: string;
  body: TermsBlock[];
}

export const TERMS_SECTIONS: readonly TermsSection[] = [
  {
    number: 1,
    title: 'Do objeto e da licença de uso',
    body: [
      {
        kind: 'p',
        text: '1.1. Este instrumento regula a concessão, pela BEM SEU TIPO PUBLICIDADE LTDA, inscrita no CNPJ sob o nº 30.305.936/0001-91 (LICENCIANTE), de licença de uso do software ATRIVA (sistema de gestão de relacionamento com clientes — CRM) em favor da CONTRATANTE, fornecido no modelo Software as a Service (SaaS) e acessível por meio da internet.',
      },
      {
        kind: 'p',
        text: '1.2. A licença ora concedida é temporária, pessoal, não exclusiva, intransferível e revogável, vigorando enquanto durar a relação contratual e limitada às finalidades próprias da Plataforma.',
      },
      {
        kind: 'p',
        text: '1.3. A adesão a estes Termos não transfere à CONTRATANTE qualquer direito sobre a propriedade intelectual, o código-fonte, a marca ou os demais elementos do ATRIVA, que permanecem de titularidade exclusiva da LICENCIANTE.',
      },
      {
        kind: 'p',
        text: '1.4. O uso da Plataforma sujeita a CONTRATANTE a todas as condições destes Termos, inclusive às regras de tratamento de dados pessoais previstas na Cláusula 7, que dela são parte indissociável.',
      },
      {
        kind: 'p',
        text: '1.5. A LICENCIANTE poderá, a qualquer tempo, promover atualizações, melhorias, alterações ou a evolução da Plataforma, tais como a inclusão, modificação ou remoção de funcionalidades, visando ao seu aprimoramento contínuo, sem que isso caracterize descumprimento destes Termos.',
      },
      {
        kind: 'p',
        text: '1.6. Estes Termos são aceitos por cada usuário no primeiro acesso e a cada nova versão publicada, na forma da Cláusula 16. O aceite manifestado por usuário que não detenha poderes de representação da CONTRATANTE vale como ciência e vinculação individual às regras de conduta aqui previstas, sem prejuízo da vinculação da CONTRATANTE pelos atos de quem a represente.',
      },
      {
        kind: 'p',
        text: '1.7. Os dados inseridos no ambiente (tenant) da CONTRATANTE — a Base de Dados — pertencem à CONTRATANTE. A marca, as cores e os domínios exibidos na Plataforma podem pertencer à organização contratante, no modelo white-label, sem que isso altere a titularidade do software prevista no item 1.3.',
      },
    ],
  },
  {
    number: 2,
    title: 'Das definições',
    body: [
      { kind: 'p', text: '2.1. Para os fins destes Termos, aplicam-se as seguintes definições:' },
      {
        kind: 'list',
        items: [
          'ATRIVA ou Plataforma — o software de gestão de relacionamento com clientes (CRM) licenciado pela LICENCIANTE, objeto deste instrumento.',
          'LICENCIANTE — a BEM SEU TIPO PUBLICIDADE LTDA, desenvolvedora e fornecedora do ATRIVA.',
          'CONTRATANTE ou USUÁRIO — a pessoa física ou jurídica que adere a estes Termos e utiliza a Plataforma.',
          'Base de Dados — o conjunto de informações, contatos, registros e dados de clientes inseridos pela CONTRATANTE na Plataforma no curso de sua utilização.',
          'Dados Pessoais — qualquer informação relacionada a pessoa natural identificada ou identificável.',
          'Dados Pessoais Sensíveis — os dados pessoais que revelem origem racial ou étnica, convicção religiosa, opinião política, filiação sindical ou a organização de caráter religioso, filosófico ou político, bem como dados referentes à saúde, à vida sexual, dado genético ou biométrico.',
          'Titular — a pessoa natural a quem se referem os Dados Pessoais tratados na Plataforma.',
          'Controlador — aquele a quem competem as decisões sobre o tratamento dos Dados Pessoais, especialmente quanto à sua finalidade.',
          'Operador — aquele que realiza o tratamento dos Dados Pessoais em nome do Controlador e conforme suas instruções.',
          'Tratamento — toda operação realizada com Dados Pessoais, como a coleta, o armazenamento, a utilização, o compartilhamento e a eliminação.',
          'LGPD — a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais) e suas alterações.',
        ],
      },
    ],
  },
  {
    number: 3,
    title: 'Do cadastro, do acesso e da segurança das credenciais',
    body: [
      {
        kind: 'p',
        text: '3.1. O acesso à Plataforma é liberado após a confirmação do pagamento e a validação dos dados de cadastro.',
      },
      {
        kind: 'p',
        text: '3.2. A CONTRATANTE é a única responsável pela veracidade, exatidão e atualização dos dados que informar no cadastro, respondendo por eventuais consequências decorrentes de informações falsas, incorretas ou desatualizadas.',
      },
      {
        kind: 'p',
        text: '3.3. As credenciais de acesso (login e senha) são pessoais e intransferíveis. A CONTRATANTE é a única responsável por sua guarda e sigilo, presumindo-se realizadas por ela todas as operações praticadas na Plataforma mediante o uso de suas credenciais.',
      },
      {
        kind: 'p',
        text: '3.4. No primeiro acesso, a CONTRATANTE recebe credenciais iniciais fornecidas pela LICENCIANTE. A senha é armazenada de forma irreversível (hash) e não é conhecida pela LICENCIANTE, que não a exibe nem a reconstitui. O esquecimento ou o extravio da senha não implica perda de dados: o acesso é restabelecido por redefinição pelo e-mail cadastrado ou por link de acesso único. Cabe à CONTRATANTE zelar por suas credenciais e por eventuais cópias de segurança das informações que julgar necessárias. Excluída a Base de Dados na forma das Cláusulas 5 e 6, não há restauração possível.',
      },
      {
        kind: 'p',
        text: '3.5. O disposto no item anterior não afasta o acesso da LICENCIANTE à infraestrutura da Plataforma, mantido de forma restrita e estritamente para fins de operação, manutenção, segurança e cumprimento das obrigações previstas nestes Termos, inclusive a exclusão da Base de Dados na hipótese da Cláusula 5.',
      },
      {
        kind: 'p',
        text: '3.6. A CONTRATANTE compromete-se a utilizar senha forte, a não compartilhá-la com terceiros, a encerrar sua sessão (logout) ao final do uso e a comunicar a LICENCIANTE, tão logo tome conhecimento, de qualquer uso não autorizado de sua conta ou de qualquer outra violação de segurança envolvendo suas credenciais.',
      },
      {
        kind: 'p',
        text: '3.7. A LICENCIANTE não se responsabiliza por perdas ou danos decorrentes do descumprimento, pela CONTRATANTE, dos deveres previstos nesta Cláusula.',
      },
      {
        kind: 'p',
        text: '3.8. Dentro do ambiente da CONTRATANTE, o papel atribuído a cada usuário (administrador, gestor, vendas ou leitura) define o alcance de dados visíveis e as ações disponíveis. É vedado contornar restrições de papel ou tentar acessar dados de outros usuários fora das regras de visibilidade configuradas. As ações realizadas na Plataforma são registradas e associadas ao usuário autenticado, e a CONTRATANTE pode suspender ou revogar contas que violem estes Termos.',
      },
    ],
  },
  {
    number: 4,
    title: 'Dos planos, do pagamento e da recorrência',
    body: [
      {
        kind: 'p',
        text: '4.1. Os serviços são contratados no modelo pré-pago, por assinatura mensal recorrente, no valor do plano selecionado no momento da contratação.',
      },
      {
        kind: 'p',
        text: '4.2. A renovação ocorre de forma automática a cada ciclo mensal, mediante cobrança na forma de pagamento cadastrada pela CONTRATANTE (cartão de crédito ou PIX, processados por meio do Asaas).',
      },
      {
        kind: 'p',
        text: '4.3. É responsabilidade da CONTRATANTE manter atualizados os dados da forma de pagamento cadastrada, de modo a viabilizar as cobranças recorrentes.',
      },
    ],
  },
  {
    number: 5,
    title: 'Da exclusão da base de dados por inadimplência',
    body: [
      {
        kind: 'p',
        text: '5.1. O não pagamento na data de vencimento caracteriza a inadimplência. Decorridos 30 (trinta) dias corridos contados do vencimento sem a regularização do débito, a LICENCIANTE encerrará o acesso da CONTRATANTE à Plataforma e procederá à exclusão definitiva da Base de Dados por ela inserida, independentemente de nova cobrança ou de qualquer formalidade adicional.',
      },
      {
        kind: 'p',
        text: '5.2. A exclusão opera pelo simples decurso do prazo previsto no item anterior. Como medida de boa-fé e transparência, a LICENCIANTE enviará à CONTRATANTE, dentro desse período, ao menos um aviso a respeito da exclusão futura, por meio do endereço eletrônico cadastrado, sem que o envio, o recebimento ou a leitura desse aviso constituam condição para a exclusão.',
      },
      {
        kind: 'p',
        text: '5.3. Durante o prazo de 30 (trinta) dias previsto no item 5.1, o acesso à Plataforma é mantido, cabendo à CONTRATANTE, com exclusividade, realizar a cópia de segurança (backup) ou a exportação da Base de Dados para outra plataforma ou meio de sua escolha, caso deseje conservá-la.',
      },
      {
        kind: 'p',
        text: '5.4. Regularizado o débito antes de decorrido o prazo do item 5.1, a relação segue seu curso normal e a Base de Dados permanece preservada.',
      },
      {
        kind: 'p',
        text: '5.5. A exclusão da Base de Dados nos termos desta Cláusula não alcança os dados cadastrais, contratuais, fiscais e de registro da própria relação entre as partes, que poderão ser conservados pela LICENCIANTE pelo prazo necessário ao cumprimento de obrigações legais e regulatórias e ao exercício regular de direitos, nos termos do art. 16 da LGPD.',
      },
    ],
  },
  {
    number: 6,
    title: 'Do cancelamento e do reembolso',
    body: [
      {
        kind: 'p',
        text: '6.1. A CONTRATANTE pode cancelar a assinatura a qualquer momento, sem multa rescisória, por meio dos canais de atendimento da LICENCIANTE (WhatsApp (17) 99719-2296 ou e-mail contato@atriva.com.br).',
      },
      {
        kind: 'p',
        text: '6.2. O cancelamento interrompe as renovações futuras, permanecendo o acesso à Plataforma ativo até o término do ciclo mensal vigente já pago.',
      },
      {
        kind: 'p',
        text: '6.3. Por se tratar de assinatura mensal pré-paga, não há devolução proporcional de valores referentes ao ciclo em curso.',
      },
      {
        kind: 'p',
        text: '6.4. Em razão do cancelamento, o acesso à Plataforma permanece ativo até o término do ciclo mensal já pago, na forma do item 6.2. A partir desse término, inicia-se o prazo de 30 (trinta) dias durante o qual a Base de Dados permanece disponível para exportação, ao fim do qual será definitivamente excluída, aplicando-se, no que couber, o disposto na Cláusula 5.',
      },
    ],
  },
  {
    number: 7,
    title: 'Da proteção de dados pessoais',
    body: [
      {
        kind: 'p',
        text: '7.1. As partes obrigam-se a tratar os dados pessoais a que tiverem acesso em razão destes Termos em conformidade com a LGPD e com as demais normas aplicáveis.',
      },
      {
        kind: 'p',
        text: '7.2. Na relação regulada por este instrumento, os papéis das partes são assim definidos:',
      },
      {
        kind: 'list',
        items: [
          'a) quanto aos dados que a CONTRATANTE insere na Plataforma sobre seus próprios clientes, contatos e terceiros (a Base de Dados), a CONTRATANTE atua como Controladora, competindo-lhe as decisões sobre a finalidade e os meios do tratamento, e a LICENCIANTE atua como Operadora, tratando tais dados exclusivamente em nome e conforme as instruções da CONTRATANTE;',
          'b) quanto aos dados de cadastro, de acesso, de pagamento e de uso da Plataforma pela própria CONTRATANTE, a LICENCIANTE atua como Controladora, tratando-os para as finalidades de execução do contrato, cobrança, segurança e aprimoramento dos serviços.',
        ],
      },
      {
        kind: 'p',
        text: '7.3. Na qualidade de Controladora da Base de Dados, a CONTRATANTE declara e garante que:',
      },
      {
        kind: 'list',
        items: [
          'a) possui base legal adequada para o tratamento dos dados pessoais que insere na Plataforma e, quando exigido, obteve o consentimento dos respectivos titulares;',
          'b) é responsável pela licitude, veracidade e atualização dos dados que insere, bem como por informar os titulares acerca do tratamento realizado;',
          'c) utilizará a Plataforma em conformidade com a LGPD, não a empregando para finalidades ilícitas, discriminatórias ou incompatíveis com as legítimas expectativas dos titulares.',
        ],
      },
      {
        kind: 'p',
        text: '7.4. A CONTRATANTE é a única responsável por eventuais dados pessoais sensíveis que venha a inserir na Plataforma, obrigando-se a observar as hipóteses e os requisitos específicos do art. 11 da LGPD. A LICENCIANTE não exige nem solicita a inserção de dados sensíveis e não responde pelo tratamento desses dados quando inseridos por iniciativa da CONTRATANTE.',
      },
      {
        kind: 'p',
        text: '7.5. Como Operadora da Base de Dados, a LICENCIANTE trata os dados pessoais nela contidos exclusivamente conforme as instruções da CONTRATANTE e para viabilizar o funcionamento da Plataforma, não os utilizando para finalidades próprias, não os comercializando e não os compartilhando com terceiros, salvo quando necessário à prestação do serviço, na forma do item 7.7, ou por exigência legal.',
      },
      {
        kind: 'p',
        text: '7.6. Os direitos previstos no art. 18 da LGPD são exercidos pelos titulares perante a respectiva Controladora. Quando o titular for cliente ou contato da CONTRATANTE, cujos dados integram a Base de Dados, as solicitações devem ser dirigidas diretamente à CONTRATANTE, cabendo à LICENCIANTE, na condição de Operadora e nos limites técnicos da Plataforma, disponibilizar os recursos necessários para que a CONTRATANTE atenda a tais solicitações. A LICENCIANTE atua, nesse âmbito, como ferramenta que intermedeia a relação entre a CONTRATANTE e os titulares, não lhe competindo decidir sobre as solicitações.',
      },
      {
        kind: 'p',
        text: '7.7. A CONTRATANTE autoriza a LICENCIANTE a contratar terceiros para a execução de atividades acessórias e necessárias à prestação do serviço — notadamente hospedagem em infraestrutura de nuvem, armazenamento de arquivos, borda de rede e proteção, processamento de pagamentos, envio de e-mails transacionais, monitoramento de erros, mensageria WhatsApp e, quando o plano contratado os incluir, provedores de inteligência artificial (geração de texto, transcrição de áudio e indexação semântica) —, os quais ficam obrigados a observar padrões de proteção de dados compatíveis com os previstos nestes Termos. Os dados transmitidos, por finalidade:',
      },
      {
        kind: 'list',
        items: [
          'Envio e recebimento de mensagens (WhatsApp, incluindo a API oficial da Meta e agregadores oficiais) — conteúdo das mensagens, telefone do destinatário, mídias e parâmetros de template.',
          'Inteligência artificial, modelos de linguagem — trechos da conversa (incluindo transcrições, o nome da pessoa indicada em cartões de contato compartilhados e o nome do local em localizações compartilhadas), dados do contato e do negócio e trechos da base de conhecimento.',
          'Inteligência artificial, transcrição de áudio — o arquivo de áudio recebido.',
          'Inteligência artificial, indexação semântica — documentos da base de conhecimento e o texto consultado.',
          'Armazenamento de arquivos — mídias recebidas nas conversas (imagens, áudios, vídeos, documentos) e arquivos de exportação.',
          'Infraestrutura de rede e proteção (CDN/WAF) — tráfego de acesso à Plataforma.',
          'Meio de pagamento — dados de faturamento da CONTRATANTE (ver Cláusula 4).',
          'Envio de e-mails transacionais — endereço do destinatário e conteúdo da mensagem.',
          'Monitoramento de erros — dados técnicos de falhas, submetidos a filtro que remove telefone, e-mail e CPF/CNPJ.',
        ],
      },
      {
        kind: 'p',
        text: 'Recursos que dependem de terceiros podem ser desativados por ambiente conforme o plano contratado — nesse caso, os dados correspondentes deixam de ser transmitidos.',
      },
      {
        kind: 'p',
        text: '7.8. A LICENCIANTE adota medidas técnicas e administrativas adequadas e compatíveis com o estado da técnica para proteger os dados pessoais tratados na Plataforma contra acessos não autorizados e situações acidentais ou ilícitas de destruição, perda, alteração ou difusão. A CONTRATANTE reconhece que nenhuma medida de segurança é capaz de assegurar proteção absoluta e que a segurança depende também da adoção, por ela própria, das cautelas previstas na Cláusula 3.',
      },
      {
        kind: 'p',
        text: '7.9. Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos titulares e que envolva a Base de Dados, a LICENCIANTE comunicará a CONTRATANTE em prazo razoável, prestando as informações de que dispuser, de modo a permitir que a CONTRATANTE, na condição de Controladora, cumpra os deveres de comunicação à Autoridade Nacional de Proteção de Dados (ANPD) e aos titulares, quando cabível.',
      },
      {
        kind: 'p',
        text: '7.10. A LICENCIANTE mantém canal de comunicação para assuntos relativos à proteção de dados pessoais, por meio do e-mail contato@atriva.com.br, por onde receberá comunicações de titulares e da ANPD, na forma do art. 41 da LGPD.',
      },
      {
        kind: 'p',
        text: '7.11. Parte dos terceiros referidos no item 7.7 opera fora do Brasil, o que caracteriza transferência internacional de dados nos termos do Capítulo V da LGPD. A LICENCIANTE adota salvaguardas compatíveis com a legislação aplicável para assegurar nível adequado de proteção nessas transferências.',
      },
      {
        kind: 'p',
        text: '7.12. A Plataforma não verifica, não coleta e não registra o consentimento do titular. Não há campo de opt-in, base legal ou data de consentimento no cadastro de contato: a verificação e a comprovação da autorização referida no item 7.3, "a", são integralmente da CONTRATANTE.',
      },
    ],
  },
  {
    number: 8,
    title: 'Da limitação de responsabilidade',
    body: [
      {
        kind: 'p',
        text: '8.1. A LICENCIANTE responde pelos danos diretos comprovadamente causados à CONTRATANTE em razão de falha na prestação dos serviços, nos termos da legislação aplicável.',
      },
      { kind: 'p', text: '8.2. A LICENCIANTE não responde por danos decorrentes de:' },
      {
        kind: 'list',
        items: [
          'a) uso da Plataforma em desacordo com estes Termos ou com a legislação aplicável;',
          'b) inserção, pela CONTRATANTE, de dados incorretos, ilícitos ou desprovidos de base legal;',
          'c) perda de informações resultante do descumprimento, pela CONTRATANTE, do dever de realizar cópia de segurança, inclusive na hipótese da Cláusula 5;',
          'd) falhas de conexão, indisponibilidades ou interrupções atribuíveis a terceiros, à infraestrutura da internet ou a caso fortuito e força maior;',
          'e) acessos não autorizados decorrentes da guarda inadequada das credenciais pela CONTRATANTE;',
          'f) restrição, bloqueio ou banimento, por terceiro, do número de WhatsApp conectado pela CONTRATANTE, ainda que decorrente do uso da Plataforma.',
        ],
      },
      {
        kind: 'p',
        text: '8.3. A Plataforma é fornecida no estado em que se encontra, não garantindo a LICENCIANTE que o serviço será ininterrupto ou inteiramente livre de falhas, comprometendo-se, contudo, a empregar seus melhores esforços para manter a disponibilidade e a corrigir, em prazo razoável, os defeitos que lhe forem reportados.',
      },
    ],
  },
  {
    number: 9,
    title: 'Do uso aceitável da Plataforma',
    body: [
      {
        kind: 'p',
        text: '9.1. A CONTRATANTE obriga-se a utilizar a Plataforma de forma lícita e compatível com a sua finalidade, comprometendo-se a não:',
      },
      {
        kind: 'list',
        items: [
          'a) empregar a Plataforma para qualquer fim ilícito, fraudulento ou que viole direitos de terceiros;',
          'b) inserir ou transmitir conteúdo que não possua ou de que não detenha os direitos ou as autorizações necessárias;',
          'c) utilizar a Plataforma para o envio de mensagens não solicitadas em desacordo com a legislação aplicável;',
          'd) inserir arquivos ou dados que contenham vírus ou outros elementos capazes de comprometer o funcionamento da Plataforma ou de sistemas de terceiros;',
          'e) interferir no funcionamento normal da Plataforma ou no uso por outros contratantes;',
          'f) tentar acessar áreas, sistemas ou dados a que não esteja autorizada.',
        ],
      },
      {
        kind: 'p',
        text: '9.2. A CONTRATANTE é a única responsável pelo conteúdo que insere e transmite por meio da Plataforma, bem como pelas obrigações perante terceiros dela decorrentes.',
      },
      {
        kind: 'p',
        text: '9.3. A LICENCIANTE poderá encerrar o acesso da CONTRATANTE que descumprir o disposto nesta Cláusula, sem prejuízo das medidas cabíveis, resguardado, sempre que possível, o direito de a CONTRATANTE exportar previamente sua Base de Dados.',
      },
    ],
  },
  {
    number: 10,
    title: 'Do WhatsApp e da mensageria',
    body: [
      {
        kind: 'p',
        text: '10.1. O envio e o recebimento de mensagens ocorrem por meio de integrações com o WhatsApp, incluindo provedores conectados por QR Code e a API oficial do WhatsApp Business.',
      },
      {
        kind: 'p',
        text: '10.2. A CONTRATANTE deve cumprir as políticas do WhatsApp e da Meta, em especial: contatar apenas destinatários com opt-in válido; não enviar mensagens em massa não solicitadas (spam) nem usar listas frias; e respeitar as janelas de atendimento e o uso de templates aprovados.',
      },
      {
        kind: 'p',
        text: '10.3. A Plataforma não substitui o julgamento da CONTRATANTE e não garante conformidade com as políticas da Meta. As restrições automáticas de janela de atendimento e de template se aplicam apenas quando a sessão usa um provedor oficial; em conexões por QR Code, nada impede tecnicamente um envio que viole as políticas.',
      },
      {
        kind: 'p',
        text: '10.4. Violações podem causar restrição ou banimento do número conectado, com impacto imediato na operação, na forma do item 8.2, "f". A CONTRATANTE é responsável pelo conteúdo que envia, inclusive quando o disparo é feito por automações, por fluxos ou por recursos de inteligência artificial ativados no seu ambiente.',
      },
      {
        kind: 'p',
        text: '10.5. Os cartões de contato e as localizações compartilhados nas conversas são registrados pela Plataforma e exibidos no histórico da conversa. Do cartão de contato, registram-se o nome, os telefones, os e-mails e a empresa da pessoa indicada; da localização, as coordenadas, o nome, o endereço e o link do local. Esses dados integram a Base de Dados mesmo quando se referem a quem não é cliente da CONTRATANTE, aplicando-se a eles os itens 7.2, "a", e 7.3.',
      },
      {
        kind: 'p',
        text: '10.6. Cadastrar como contato a pessoa indicada em um cartão a inclui entre os contatos da Base de Dados, sem criar negócio. O compartilhamento do cartão por outra pessoa não constitui consentimento nem opt-in da pessoa indicada. Antes de contatá-la, inclusive por automações e fluxos acionados pela criação de contato, a CONTRATANTE deve assegurar-se de que possui base legal para fazê-lo, observado o item 10.2.',
      },
    ],
  },
  {
    number: 11,
    title: 'Da inteligência artificial',
    body: [
      {
        kind: 'p',
        text: '11.1. A Plataforma oferece recursos de IA habilitáveis por plano e por ambiente:',
      },
      {
        kind: 'list',
        items: [
          'Copiloto — sugere respostas e resumos, sob comando do usuário.',
          'Autopiloto — responde automaticamente às mensagens recebidas.',
          'Lead Scoring — pontua e classifica leads a partir das conversas.',
          'Transcrição — converte em texto os áudios recebidos.',
          'SDR autônomo — qualifica, acompanha, retoma contato e executa ações no CRM dentro das permissões configuradas.',
        ],
      },
      {
        kind: 'p',
        text: '11.2. Autopiloto e SDR autônomo enviam mensagens sem aprovação prévia humana, e o acompanhamento automático de leads inativos pode iniciar o contato. Cabe à CONTRATANTE decidir se ativa esses recursos e configurar seu comportamento.',
      },
      { kind: 'p', text: '11.3. Em contrapartida, a Plataforma garante que:' },
      {
        kind: 'list',
        items: [
          'o usuário pode assumir a conversa a qualquer momento, o que interrompe a atuação automática naquele atendimento;',
          'as ações que a IA pode executar no CRM são restritas a uma lista autorizada pela CONTRATANTE, que não inclui exclusão de dados nem a decisão de ganhar ou perder um negócio;',
          'toda interação e ação de IA é registrada e auditável, com identificação do contato, do conteúdo gerado e do resultado.',
        ],
      },
      {
        kind: 'p',
        text: '11.4. Mensagens geradas por IA são identificadas como tal na interface do CRM para os operadores. O processamento por provedores terceiros observa os itens 7.7 e 7.11.',
      },
    ],
  },
  {
    number: 12,
    title: 'Das automações e dos fluxos',
    body: [
      {
        kind: 'p',
        text: '12.1. Regras criadas pelo usuário ou pela CONTRATANTE executam ações automáticas — envio de mensagens, movimentação de negócios no pipeline, criação de tarefas e notas, entre outras. Toda execução gera registro, e o motor aplica limites de encadeamento, de repetição por contato e proteção contra laços infinitos.',
      },
      {
        kind: 'p',
        text: '12.2. A CONTRATANTE responde pelas regras que criar, inclusive pelo conteúdo automatizado entregue a contatos, ao qual se aplicam integralmente as obrigações da Cláusula 10.',
      },
    ],
  },
  {
    number: 13,
    title: 'Da captação e da atribuição de leads',
    body: [
      {
        kind: 'p',
        text: '13.1. A Plataforma pode receber leads de fontes externas conectadas pela CONTRATANTE (anúncios e formulários de Meta, Google e TikTok, formulários em site e e-mail de entrada) e registrar dados de atribuição de campanhas e anúncios, incluindo anúncios de clique para WhatsApp.',
      },
      {
        kind: 'p',
        text: '13.2. Esses dados chegam à Plataforma de duas formas. Na primeira, a Plataforma consulta a fonte externa conectada para obter os dados preenchidos pelo lead; credenciais de integração são armazenadas de forma cifrada. Na segunda, a própria CONTRATANTE — diretamente ou por meio de integração ou serviço que ela contrate — envia dados de atribuição à Plataforma pela API pública; nessa hipótese, o conteúdo enviado é determinado exclusivamente pela CONTRATANTE, e aplica-se integralmente a Cláusula 14.',
      },
      {
        kind: 'p',
        text: '13.3. A Plataforma registra os dados de atribuição como recebidos e não verifica sua exatidão. Em qualquer das formas, os dados devem ser usados apenas para a finalidade comercial da CONTRATANTE e observam a Cláusula 7.',
      },
    ],
  },
  {
    number: 14,
    title: 'Da API pública, dos webhooks e das exportações',
    body: [
      {
        kind: 'p',
        text: '14.1. A CONTRATANTE pode emitir chaves de API com escopos definidos, configurar webhooks assinados para sistemas externos e exportar relatórios.',
      },
      {
        kind: 'p',
        text: '14.2. Dados entregues a sistemas externos ou exportados passam a estar sob a responsabilidade de quem os recebe e armazena — a Plataforma não controla o uso posterior. A CONTRATANTE deve proteger chaves de API e arquivos exportados conforme a LGPD e suas próprias políticas, e revogar chaves comprometidas imediatamente.',
      },
    ],
  },
  {
    number: 15,
    title: 'Dos registros, da auditoria e da retenção',
    body: [
      {
        kind: 'p',
        text: '15.1. Ações relevantes geram registros de auditoria: eventos de domínio, histórico de execução de automações e fluxos, entregas de webhook e interações de IA. Esses registros são usados para segurança, auditoria, suporte e apuração de incidentes.',
      },
      { kind: 'p', text: '15.2. A CONTRATANTE deve estar ciente de que:' },
      {
        kind: 'list',
        items: [
          'os registros de eventos são imutáveis e preservam o estado do dado no momento do fato, inclusive após a exclusão do contato correspondente na interface;',
          'a exclusão de contatos e negócios pelo CRM é reversível (o registro é marcado como excluído e pode ser restaurado);',
          'apagar uma mensagem apenas para você a oculta da interface sem removê-la do histórico;',
          'os registros técnicos mascaram credenciais e segredos; dados de falhas enviados à ferramenta de monitoramento passam por filtro adicional que remove telefone, e-mail e CPF/CNPJ.',
        ],
      },
      {
        kind: 'p',
        text: '15.3. O disposto nesta Cláusula não afasta a exclusão definitiva da Base de Dados prevista nas Cláusulas 5 e 6.',
      },
    ],
  },
  {
    number: 16,
    title: 'Das disposições gerais, do aceite e do foro',
    body: [
      {
        kind: 'p',
        text: '16.1. A LICENCIANTE poderá atualizar estes Termos periodicamente, para refletir aprimoramentos da Plataforma ou adequações legais e regulatórias. As alterações substanciais serão comunicadas à CONTRATANTE pelo endereço eletrônico cadastrado ou por aviso na própria Plataforma. Estes Termos são versionados por data, e a publicação de nova versão exige novo aceite expresso no próximo acesso, com destaque do que mudou; até o aceite, o uso do CRM permanece bloqueado. A recusa desconecta o usuário e impede o uso do CRM até que haja aceite, podendo ser revista a qualquer momento. O histórico de aceites e recusas — usuário, versão, data e hora — fica registrado para fins de auditoria.',
      },
      {
        kind: 'p',
        text: '16.2. A tolerância quanto ao descumprimento de qualquer disposição destes Termos não implica novação nem renúncia, permanecendo as condições aqui previstas em pleno vigor.',
      },
      {
        kind: 'p',
        text: '16.3. A eventual nulidade ou invalidade de qualquer disposição destes Termos não prejudica as demais, que permanecem válidas e eficazes.',
      },
      {
        kind: 'p',
        text: '16.4. Fica eleito o foro da Comarca de São José do Rio Preto/SP para dirimir as controvérsias decorrentes destes Termos.',
      },
    ],
  },
];

export interface TermsChangelogEntry {
  /** Versão publicada (`YYYY-MM-DD`), igual à do documento. */
  version: string;
  /** O que mudou EM RELAÇÃO à versão anterior, em linguagem de usuário. */
  changes: string[];
}

/**
 * Changelog por versão — alimenta a caixa "O que mudou nesta versão" mostrada a
 * quem já havia aceitado uma versão anterior.
 *
 * ⚠️ CADA bump de versão adiciona UMA entrada nova no topo desta lista,
 * espelhando a tabela de Changelog de `docs/legal/TERMS_OF_USE.md`. Sem a
 * entrada, o modal de re-aceite aparece sem o resumo das mudanças (degrada,
 * não quebra).
 */
export const TERMS_CHANGELOG: readonly TermsChangelogEntry[] = [
  {
    version: '2026-09-22',
    changes: [
      'Cartões de contato e localizações compartilhados nas conversas passam a ser registrados e exibidos, mesmo quando se referem a quem não é cliente da organização; o cartão não é consentimento da pessoa indicada, e contatá-la exige base legal.',
      'A inteligência artificial passa a receber o nome da pessoa indicada no cartão e o nome do local compartilhado.',
    ],
  },
  {
    version: '2026-09-09',
    changes: [
      'Revisão jurídica completa. Os termos passam a identificar a empresa licenciante e a reunir num só documento as condições de contratação: licença de uso, cadastro e credenciais, planos e recorrência, cancelamento e reembolso.',
      'Passa a haver prazo de exclusão: encerrado o acesso por cancelamento ou por inadimplência, a base de dados é definitivamente excluída após 30 dias, cabendo à sua organização exportar dentro desse prazo o que quiser conservar.',
      'A cláusula de proteção de dados passa a definir expressamente os papéis de controlador e operador, o tratamento de dados sensíveis, a comunicação de incidentes de segurança e o canal da ANPD.',
      'Fica eleito o foro da Comarca de São José do Rio Preto/SP.',
    ],
  },
  {
    version: '2026-08-19',
    changes: [
      'A organização passou a poder enviar dados de origem e atribuição de leads (campanha, anúncio e parâmetros de rastreamento do link) por integração própria, além de a Plataforma buscá-los nas fontes conectadas; o conteúdo enviado dessa forma é de responsabilidade da organização.',
    ],
  },
  {
    version: '2026-08-18',
    changes: [
      // Versão inicial: não existe versão anterior para comparar, então ninguém
      // chega ao estado de re-aceite nesta versão. A entrada existe para manter
      // o histórico completo e o formato pronto para o próximo bump.
      'Versão inicial dos Termos de Uso da Plataforma.',
    ],
  },
];

/** Mudanças de uma versão específica; `undefined` quando não há entrada. */
export function getTermsChanges(version: string): string[] | undefined {
  return TERMS_CHANGELOG.find((entry) => entry.version === version)?.changes;
}

/**
 * Tempo estimado de leitura, em minutos (mín. 1), a 200 palavras/min.
 * Calculado do próprio texto para não virar um número solto que envelhece a
 * cada revisão das seções.
 */
export const TERMS_READING_MINUTES: number = (() => {
  const words = TERMS_SECTIONS.reduce((total, section) => {
    const sectionText = section.body
      .map((block) => (block.kind === 'p' ? block.text : block.items.join(' ')))
      .join(' ');
    return total + `${section.title} ${sectionText}`.split(/\s+/).length;
  }, 0);
  return Math.max(1, Math.round(words / 200));
})();
